import { BreakdownDisplayMode } from "@redotech/cockroachdb-codegen/tables/xborder-outbound/breakdown-display-mode";
import { LandedCostItem } from "@redotech/cockroachdb-codegen/tables/xborder-outbound/landed-cost-item";
import { MarketPricingMode } from "@redotech/cockroachdb-codegen/tables/xborder-outbound/market-pricing-mode";
import { MarkupStrategy } from "@redotech/cockroachdb-codegen/tables/xborder-outbound/markup-strategy";
import {
  COVERED_BUT_NOT_CHARGED_MESSAGE,
  deriveMarketPricingMode,
  INCLUDED_AND_EXCLUDED_MESSAGE,
  inclusionShapeError,
  marketDefinitionConflicts,
} from "@redotech/redo-xborder-outbound-inclusive-pricing/market-definition";
import * as z from "zod";

// Re-export so wire consumers (e.g. the markets editor) reference enum members
// instead of re-typing the literals.

export const landedCostItem = z.enum(LandedCostItem);

// Fees are cart-level (per-shipment) and can never be baked into a per-item price.
export const priceableLandedCostItem = z.enum([
  LandedCostItem.Duties,
  LandedCostItem.Taxes,
]);

export const markupStrategy = z.enum(MarkupStrategy);

export const marketPricingMode = z.enum(MarketPricingMode);

export const breakdownDisplayMode = z.enum(BreakdownDisplayMode);

export const breakdownLabelOverrides = z.object({
  shipping: z.string().trim().min(1).max(80).optional(),
  taxes: z.string().trim().min(1).max(80).optional(),
  dutiesAndFees: z.string().trim().min(1).max(80).optional(),
});
export interface LandedCostBreakdownLabelOverrides extends z.infer<
  typeof breakdownLabelOverrides
> {}

export function refineMarketInclusion(
  value: {
    pricingMode?: MarketPricingMode | undefined;
    includeInPrice?: LandedCostItem[] | undefined;
    coverWithMarkup?: LandedCostItem[] | undefined;
    excludeInCheckout?: LandedCostItem[] | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  const { includeInPrice, pricingMode } = value;
  if (includeInPrice === undefined) {
    if (pricingMode === MarketPricingMode.AdaptiveCoefficient) {
      ctx.addIssue({
        code: "custom",
        message:
          "Adaptive coefficient requires includeInPrice: taxes alone, or " +
          "duties, taxes and fees together",
        path: ["includeInPrice"],
      });
    }
    return;
  }
  const shapeError = inclusionShapeError(
    includeInPrice,
    pricingMode ?? deriveMarketPricingMode(includeInPrice),
  );
  if (shapeError) {
    ctx.addIssue({
      code: "custom",
      message: shapeError,
      path: ["includeInPrice"],
    });
  }
  const conflicts = marketDefinitionConflicts(
    includeInPrice,
    value.coverWithMarkup ?? [],
    value.excludeInCheckout ?? [],
  );
  if (conflicts.coveredButNotCharged.length > 0) {
    ctx.addIssue({
      code: "custom",
      message: `${COVERED_BUT_NOT_CHARGED_MESSAGE}: ${conflicts.coveredButNotCharged.join(", ")}`,
      path: ["coverWithMarkup"],
    });
  }
  if (conflicts.includedAndExcluded.length > 0) {
    ctx.addIssue({
      code: "custom",
      message: `${INCLUDED_AND_EXCLUDED_MESSAGE}: ${conflicts.includedAndExcluded.join(", ")}`,
      path: ["excludeInCheckout"],
    });
  }
}

export const marketDefinitionWireFields = {
  pricingMode: marketPricingMode,
  markup: z.number(),
  markupStrategy,
  includeInPrice: z.array(landedCostItem),
  excludeInCheckout: z.array(landedCostItem),
  coverWithMarkup: z.array(landedCostItem),
  breakdownDisplayMode,
  breakdownLabelOverrides,
};

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { BreakdownDisplayMode } from "@redotech/cockroachdb-codegen/tables/xborder-outbound/breakdown-display-mode";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { LandedCostItem } from "@redotech/cockroachdb-codegen/tables/xborder-outbound/landed-cost-item";
// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { MarkupStrategy } from "@redotech/cockroachdb-codegen/tables/xborder-outbound/markup-strategy";
